import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../api.js";
import { supabase } from "../lib/supabase.js";
import { fixImageUrl } from "../utils/fixImageUrl.js";
import "./ArtistProfile.css";

const IS_UUID     = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const IS_OBJECTID = /^[0-9a-fA-F]{24}$/;

export default function ArtistProfile() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar" || i18n.language?.startsWith("ar");

  const { id } = useParams();
  const location = useLocation();
  const justCreated = location.state?.justCreated;

  const [profile,  setProfile]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [imgModal, setImgModal] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (IS_UUID.test(id)) {
          // Supabase artist
          const { data } = await supabase.from("artists").select("*").eq("id", id).single();
          if (data) { setProfile({ ...data, _source: "supabase" }); return; }
        }
        if (IS_OBJECTID.test(id)) {
          // MongoDB artist
          const res = await axios.get(`${API_BASE}/getRally/${id}`);
          if (res.data) { setProfile({ ...res.data, _source: "mongo" }); return; }
        }
        // If neither matched, try both
        const [supaRes, mongoRes] = await Promise.allSettled([
          supabase.from("artists").select("*").eq("id", id).single(),
          axios.get(`${API_BASE}/getRally/${id}`),
        ]);
        if (supaRes.status === "fulfilled" && supaRes.value.data) {
          setProfile({ ...supaRes.value.data, _source: "supabase" });
        } else if (mongoRes.status === "fulfilled") {
          setProfile({ ...mongoRes.value.data, _source: "mongo" });
        }
      } catch {
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
    else setLoading(false);
  }, [id]);

  if (loading) return (
    <div style={{ textAlign: "center", padding: "80px 20px", color: "var(--text-3)" }}>
      {t("artists.loading")}
    </div>
  );

  if (!profile) return (
    <div style={{ textAlign: "center", padding: "80px 20px", color: "var(--text-3)" }}>
      Artist not found.
    </div>
  );

  const fromSupa = profile._source === "supabase";

  const displayName     = isAr ? (profile.name_ar    || profile.name)      : profile.name;
  const displaySpec     = isAr ? (profile.specialty_ar || profile.specialty) : profile.specialty;
  const displayBio      = isAr ? (profile.bio_ar      || profile.bio      || profile.biography)
                               : (profile.bio         || profile.biography || "");
  const displayIntro    = isAr ? (profile.intro_ar    || profile.intro)    : (profile.intro || "");
  const avatarSrc       = fixImageUrl(profile.photo_url || profile.profileImage);
  const images          = Array.isArray(profile.images) ? profile.images : [];

  return (
    <div className="profile-page spar-page">
      {justCreated && (
        <div className="spar-alert spar-alert-success" style={{ marginBottom: 28 }}>
          {t("signup.success")}
        </div>
      )}

      <a href="/ArtistsList" className="profile-back">
        ← {t("profile.back")}
      </a>

      <div className="spar-card profile-card">
        <div className="profile-header">
          <div className="profile-header-bg" />
          <div className="profile-header-content">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={displayName}
                className="profile-avatar"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="profile-avatar profile-avatar-placeholder">
                {profile.name?.[0] || "?"}
              </div>
            )}
            <div className="profile-identity">
              {(fromSupa ? profile.is_elder : true) && (
                <span className="spar-gold-badge">{t("profile.goldenMember")}</span>
              )}
              <h1 className="profile-name">{displayName}</h1>
              {displaySpec  && <p className="profile-specialty">{displaySpec}</p>}
              {profile.country && <p className="profile-country">{profile.country}</p>}
            </div>
          </div>
        </div>

        <div className="profile-body">
          {(displayIntro || displayBio) && (
            <section className="profile-section">
              <h2 className="profile-section-title">
                {t("profile.about")} {displayName}
              </h2>
              {displayIntro && <p className="profile-bio" style={{ marginBottom: 12 }}>{displayIntro}</p>}
              {displayBio   && <p className="profile-bio">{displayBio}</p>}
            </section>
          )}

          {images.length > 0 && (
            <section className="profile-section">
              <h2 className="profile-section-title">{t("profile.portfolio")}</h2>
              <div className="profile-grid">
                {images.slice(0, 8).map((img, i) => {
                  const src = fixImageUrl(img);
                  return (
                    <button
                      key={i}
                      className="profile-img-btn"
                      onClick={() => setImgModal(src)}
                      aria-label={`View artwork ${i + 1}`}
                    >
                      <img src={src} alt={`${displayName} artwork ${i + 1}`} className="profile-img" />
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section className="profile-section">
            <h2 className="profile-section-title">{t("profile.contact")}</h2>
            <div className="profile-contact-grid">
              {profile.address && (
                <div className="profile-contact-item">
                  <span className="profile-contact-label">{t("signup.address")}</span>
                  <span>{profile.address}</span>
                </div>
              )}
              {profile.telephone && (
                <div className="profile-contact-item">
                  <span className="profile-contact-label">{t("signup.telephone")}</span>
                  <a href={`tel:${profile.telephone}`}>{profile.telephone}</a>
                </div>
              )}
              {profile.email && (
                <div className="profile-contact-item">
                  <span className="profile-contact-label">{t("signup.email")}</span>
                  <a href={`mailto:${profile.email}`}>{profile.email}</a>
                </div>
              )}
              {profile.website && (
                <div className="profile-contact-item">
                  <span className="profile-contact-label">{t("profile.website")}</span>
                  <a href={profile.website} target="_blank" rel="noreferrer">
                    {profile.website.replace(/^https?:\/\//, "")}
                  </a>
                </div>
              )}
              {profile.instagram && (
                <div className="profile-contact-item">
                  <span className="profile-contact-label">{t("profile.instagram")}</span>
                  <a href={`https://instagram.com/${profile.instagram.replace("@", "")}`}
                    target="_blank" rel="noreferrer">{profile.instagram}</a>
                </div>
              )}
              {profile.facebook && (
                <div className="profile-contact-item">
                  <span className="profile-contact-label">{t("profile.facebook")}</span>
                  <a href={profile.facebook} target="_blank" rel="noreferrer">{profile.facebook}</a>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {imgModal && (
        <div className="profile-lightbox" onClick={() => setImgModal(null)}>
          <img src={imgModal} alt="artwork" className="profile-lightbox-img" />
        </div>
      )}
    </div>
  );
}
